"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { EMessageType, IMessage } from "types/Message.type";
import { IUser } from "types/User.types";
import LazyMessageRenderer from "./LazyMessageRenderer";

interface EventCardSliderProps {
  messages: IMessage[];
  mentionUsers: IUser[];
  allMessages: IMessage[];
  setMessages: (messages: IMessage[]) => void;
  vendor?: any;
}

const DURATION = 3000;

const getDateSortValue = (date?: Date | string | null) => {
  if (!date) return 0;

  const parsedDate = new Date(date);

  return Number.isNaN(parsedDate.getTime()) ? 0 : parsedDate.getTime();
};

const getRecurringEventSortValue = (
  eventDate?: Date | string | null,
  referenceDate?: Date | string | null
) => {
  if (!eventDate) return 0;

  const parsedEventDate = new Date(eventDate);
  const parsedReferenceDate = referenceDate
    ? new Date(referenceDate)
    : new Date();

  if (Number.isNaN(parsedEventDate.getTime())) return 0;

  const referenceYear = Number.isNaN(parsedReferenceDate.getTime())
    ? new Date().getFullYear()
    : parsedReferenceDate.getFullYear();

  // Birthdays/anniversaries are recurring yearly events. Compare the full
  // event occurrence date for the feed year, not only the day number and not
  // the original birth/activeSince year.
  return new Date(
    referenceYear,
    parsedEventDate.getMonth(),
    parsedEventDate.getDate()
  ).getTime();
};

const getEventSortValue = (message: IMessage) => {
  if (message.type === EMessageType.BIRTHDAY) {
    return getRecurringEventSortValue(
      message.createdBy?.birthday ?? message.createdBy?.birthDate,
      message.createdAt
    );
  }

  if (message.type === EMessageType.ANNIVERSARY) {
    return getRecurringEventSortValue(
      message.createdBy?.activeSince,
      message.createdAt
    );
  }

  return getDateSortValue(message.createdAt);
};

const sortNewestFirst = (a: IMessage, b: IMessage) => {
  const eventSortDifference = getEventSortValue(b) - getEventSortValue(a);

  if (eventSortDifference !== 0) return eventSortDifference;

  return getDateSortValue(b.createdAt) - getDateSortValue(a.createdAt);
};

const KEYFRAMES = `
  @keyframes ecs-out {
    0%   { opacity:1; transform: perspective(700px) rotateY(0deg) translateX(0px) scale(1); }
    100% { opacity:0; transform: perspective(700px) rotateY(-18deg) translateX(-28px) scale(0.95); }
  }
  @keyframes ecs-in {
    0%   { opacity:0; transform: perspective(700px) rotateY(18deg) translateX(28px) scale(0.95); }
    100% { opacity:1; transform: perspective(700px) rotateY(0deg) translateX(0px) scale(1); }
  }
  @keyframes ecs-fill {
    from { transform: scaleX(0); }
    to   { transform: scaleX(1); }
  }
`;

export function EventCardSlider({
  messages,
  mentionUsers,
  allMessages,
  setMessages,
  vendor,
}: EventCardSliderProps) {
  const [current, setCurrent] = useState(0);
  const [phase, setPhase] = useState<"idle" | "out" | "in">("idle");
  const [paused, setPaused] = useState(false);
  const [fillKey, setFillKey] = useState(0);
  const [slideHeights, setSlideHeights] = useState<number[]>([]);
  const [maxSlideHeight, setMaxSlideHeight] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const orderedMessages = useMemo(
    () => [...messages].sort(sortNewestFirst),
    [messages]
  );
  const count = orderedMessages.length;

  const goTo = useCallback(
    (next: number) => {
      if (phase !== "idle" || next === current) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      setPhase("out");
      setTimeout(() => {
        setCurrent(next);
        setFillKey((k) => k + 1);
        setPhase("in");
        setTimeout(() => setPhase("idle"), 400);
      }, 340);
    },
    [phase, current]
  );

  useEffect(() => {
    setCurrent(0);
    setFillKey((key) => key + 1);
  }, [orderedMessages]);

  useLayoutEffect(() => {
    const measureNode = measureRef.current;

    if (!measureNode || count === 0) {
      setMaxSlideHeight(null);
      return;
    }

    let frameId = 0;

    const measureSlides = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        const heights = Array.from(measureNode.children).map((child) =>
          Math.ceil(child.getBoundingClientRect().height)
        );
        const nextHeight = Math.max(...heights, 0);

        setSlideHeights((currentHeights) =>
          currentHeights.length === heights.length &&
          currentHeights.every((height, index) => height === heights[index])
            ? currentHeights
            : heights
        );
        setMaxSlideHeight((currentHeight) =>
          currentHeight === nextHeight ? currentHeight : nextHeight || null
        );
      });
    };

    measureSlides();

    const resizeObserver = new ResizeObserver(measureSlides);

    Array.from(measureNode.children).forEach((child) => {
      resizeObserver.observe(child);
    });

    if (rootRef.current) {
      resizeObserver.observe(rootRef.current);
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
    };
  }, [count, orderedMessages]);

  // Auto-advance
  useEffect(() => {
    if (paused || count <= 1 || phase !== "idle") return;
    timerRef.current = setTimeout(() => {
      goTo((current + 1) % count);
    }, DURATION);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [current, paused, count, phase, goTo]);

  const cardAnimation: React.CSSProperties =
    phase === "out"
      ? { animation: "ecs-out 0.34s cubic-bezier(0.4,0,0.6,1) forwards" }
      : phase === "in"
      ? { animation: "ecs-in 0.40s cubic-bezier(0.2,0,0.4,1) forwards" }
      : { opacity: 1, transform: "none" };

  const currentSlideHeight = slideHeights[current] ?? maxSlideHeight ?? 0;
  const stackExtraSpace = Math.max((maxSlideHeight ?? 0) - currentSlideHeight, 0);
  const visibleSlideStyle = {
    ...(maxSlideHeight ? { height: maxSlideHeight } : {}),
    "--ecs-stack-extra-space": `${stackExtraSpace}px`
  } as React.CSSProperties;

  return (
    <div
      ref={rootRef}
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <style>{`${KEYFRAMES}
        .ecs-stack-spacer {
          height: var(--ecs-stack-extra-space, 0px);
          flex-shrink: 0;
          transition: height 300ms ease-out;
        }
      `}</style>

      {/* ── Progress bar (stories style) ────────────────────────────── */}
      {count > 1 && (
        <div className="mb-3 flex items-center gap-1.5 px-0.5">
          {orderedMessages.map((_, i) => (
            <button
              key={i}
              aria-label={`Ir a tarjeta ${i + 1}`}
              onClick={() => goTo(i)}
              className="relative h-[3px] flex-1 overflow-hidden rounded-full bg-slate-200/80 dark:bg-zinc-700/60"
            >
              {/* Past segment */}
              {i < current && (
                <div className="absolute inset-0 bg-slate-400/80 dark:bg-zinc-400/70" />
              )}
              {/* Active segment — animating fill */}
              {i === current && !paused && (
                <div
                  key={fillKey}
                  className="absolute inset-y-0 left-0 w-full origin-left bg-slate-500/90 dark:bg-zinc-300/80"
                  style={{
                    animation: `ecs-fill ${DURATION}ms linear forwards`,
                  }}
                />
              )}
              {/* Paused active segment */}
              {i === current && paused && (
                <div
                  className="absolute inset-0 bg-slate-400/80 dark:bg-zinc-400/70"
                  style={{ width: "50%" }}
                />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Hidden measurement layer: keeps the visible slider height equal to
          the tallest card in this stack, preventing feed jumps between slides. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute left-0 top-0 -z-10 w-full"
      >
        {orderedMessages.map((message) => (
          <div key={`measure-${message.id}`} className="w-full">
            <LazyMessageRenderer
              message={message}
              mentionUsers={mentionUsers}
              messages={allMessages}
              setMessages={setMessages}
              vendor={vendor}
              stacked
            />
          </div>
        ))}
      </div>

      {/* ── Card ────────────────────────────────────────────────────── */}
      <div
        className="relative transition-[height] duration-300 ease-out"
        style={visibleSlideStyle}
      >
        <div className="h-full" style={cardAnimation}>
          <LazyMessageRenderer
            message={orderedMessages[current]}
            mentionUsers={mentionUsers}
            messages={allMessages}
            setMessages={setMessages}
            vendor={vendor}
            stacked
          />
        </div>
      </div>

      {/* ── Dot navigation ──────────────────────────────────────────── */}
      {count > 1 && (
        <div className="mt-2.5 flex items-center justify-center gap-1.5">
          {orderedMessages.map((_, i) => (
            <button
              key={i}
              aria-label={`Tarjeta ${i + 1}`}
              onClick={() => goTo(i)}
              className={`rounded-full transition-all duration-300 ease-out ${
                i === current
                  ? "h-[5px] w-6 bg-slate-500/80 dark:bg-zinc-300/80"
                  : "h-[5px] w-[5px] bg-slate-300/80 hover:bg-slate-400/80 dark:bg-zinc-600/70 dark:hover:bg-zinc-500/70"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
